import { NO_DATA_VALUE_FOR_SORTING } from 'constants/dashboard';
import { REQUEST_METHODS } from 'constants/api';
import { API_TAG_TYPES } from 'constants/apiTagTypes';
import {
  formatHeatmapData,
  formatKeyEventsData,
  formatMasterDashboardHeatmapData,
  formatPillarExperienceTracker,
} from 'helper/formatDashboardBucketData';
import {
  formatBucketsData,
  formatCommunityData,
  formatCulturalData,
  formatMasterDashboardData,
  formatSampleData,
  formatWordCloudData,
} from 'helper/formatDashboardData';
import { baseApi } from 'store/baseApi';

export const dashboardApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getDashboardPortfolio: build.query({
      query: ({ organisationId, params }) => ({
        url: `/dashboard/${organisationId}/dashboard-portfolio/`,
        params,
      }),
      transformResponse: (baseQueryReturnValue) =>
        baseQueryReturnValue.map(({ data, ...rest }) => ({
          ...data,
          participation:
            data.participation || data.participation === 0 ? data.participation : NO_DATA_VALUE_FOR_SORTING,
          growing: data.growing || data.growing === 0 ? data.growing : NO_DATA_VALUE_FOR_SORTING,
          projectName: rest.name,
          launchDate: rest.launch_date,
          id: rest.id,
          status: rest.status,
          surveysNumber: rest.surveys_number,
        })),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: API_TAG_TYPES.DASHBOARD_PORTFOLIO, id })),
              { type: API_TAG_TYPES.DASHBOARD_PORTFOLIO, id: 'LIST' },
            ]
          : [{ type: API_TAG_TYPES.DASHBOARD_PORTFOLIO, id: 'LIST' }],
    }),
    getPersonalDrilldown: build.query({
      query: ({ projectId, params }) => ({
        url: `/buckets/drilldown/${projectId}/personal/`,
        params,
      }),
      transformResponse: (baseQueryReturnValue) => {
        const datasets = baseQueryReturnValue.datasets ?? [];
        const pillarExperienceTracker = baseQueryReturnValue.pillar_experience_tracker ?? [];

        return {
          projectId: baseQueryReturnValue.project_id,
          datasets: datasets.map((datasetDate) => +new Date(datasetDate)),
          organisationId: baseQueryReturnValue.organisation_id,
          projectName: baseQueryReturnValue.project_name ?? '',
          pillarExperienceTracker: pillarExperienceTracker.map(({ name, points = [] }) => ({
            name,
            points: datasets.map((date) => {
              const pillarsData = points[date] ?? [];

              return {
                name: date,
                pillars: pillarsData.map(({ rate, pillar, color }) => ({
                  name: pillar,
                  value: rate,
                  color,
                })),
              };
            }),
          })),
        };
      },
      providesTags: (result, _error, { projectId }) => [
        { type: API_TAG_TYPES.PERSONAL_TRACKER, id: result?.projectId ?? projectId },
      ],
    }),
    getDatasetDates: build.query({
      query: (params) => ({
        url: `/dashboard/get-all-dataset-dates/`,
        params,
      }),
      providesTags: (_result, _error, { project_id: projectId }) => [
        { type: API_TAG_TYPES.PROJECT_DASHBOARD, id: projectId },
      ],
    }),
    getDashboard: build.query({
      query: (params) => ({
        url: '/dashboard/',
        params,
      }),
      transformResponse: (baseQueryReturnValue) => {
        const {
          project_id: projectId,
          project_name: projectName,
          organisation_id: organisationId,
          organisation_name: organisationName,
          project_status: projectStatus,
          dashboard_data: dashboardData,
        } = baseQueryReturnValue;

        return {
          projectId,
          projectName,
          organisationId,
          organisationName,
          projectStatus,
          dashboardData: {
            cultural: formatCulturalData(dashboardData.cultural_performance),
            sampleArr: formatSampleData(dashboardData.groups ?? []),
            buckets: formatBucketsData(dashboardData.buckets || []),
            community: formatCommunityData(dashboardData.community_insights),
            words: formatWordCloudData(dashboardData.words),
          },
        };
      },
      providesTags: (result, _error, { project_id: projectId }) => [
        { type: API_TAG_TYPES.PROJECT_DASHBOARD, id: result?.projectId ?? projectId },
      ],
    }),
    getDrilldown: build.query({
      query: ({ id, params }) => ({
        url: `/buckets/drilldown/key-areas/${id}/graph-data/`,
        params,
      }),
      transformResponse: (baseQueryReturnValue) => {
        const {
          key_area_name: bucketName,
          project_name: projectName,
          project_id: projectId,
          datasets = [],
        } = baseQueryReturnValue;

        return {
          bucketName,
          projectName,
          projectId,
          dateTicks: !datasets.length ? [] : datasets.map((datasetDate) => +new Date(datasetDate)),
          pillarExperienceTracker: formatPillarExperienceTracker(baseQueryReturnValue.pillar_experience_tracker || []),
          keyEventData: formatKeyEventsData(baseQueryReturnValue.events_stats || []),
        };
      },
      providesTags: (result, _error, { id }) => [{ type: API_TAG_TYPES.PROJECT_DASHBOARD_KEY_AREA_GRAPH, id }],
    }),
    getHeatmap: build.query({
      query: ({ id, params }) => ({
        url: `/buckets/drilldown/key-areas/${id}/heatmap/`,
        params,
      }),
      transformResponse: (baseQueryReturnValue) => formatHeatmapData(baseQueryReturnValue),
      providesTags: (result, _error, { id }) => [{ type: API_TAG_TYPES.PROJECT_DASHBOARD_KEY_AREA_HEATMAP, id }],
    }),
    getMasterDashboardsList: build.query({
      query: (params) => ({
        url: '/dashboard/master-dashboard/',
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: API_TAG_TYPES.MASTER_DASHBOARD, id })),
              { type: API_TAG_TYPES.MASTER_DASHBOARD, id: 'LIST' },
            ]
          : [{ type: API_TAG_TYPES.MASTER_DASHBOARD, id: 'LIST' }],
      transformResponse: (baseQueryReturnValue = []) =>
        baseQueryReturnValue.map((masterDashboardData) => ({
          id: masterDashboardData.id,
          name: masterDashboardData.name,
          createdAt: masterDashboardData.created_at,
          projectsCount: masterDashboardData.project_details?.length || 0,
          projects: masterDashboardData.project_details?.map(({ id }) => id) || [],
        })),
    }),
    getMasterDashboardProjects: build.query({
      query: (params) => ({
        url: '/dashboard/master-dashboard/projects-list/',
        params,
      }),
      transformResponse: (baseQueryReturnValue) => ({
        structureGroups: baseQueryReturnValue.structure_groups,
        projects: baseQueryReturnValue.projects.map((project) => ({
          id: project.id,
          name: project.name,
          status: project.status,
          launchDate: project.launch_date,
          isPublic: project.is_public,
          structureId: project.structure_id,
        })),
      }),
      providesTags: [{ type: API_TAG_TYPES.MASTER_DASHBOARD_CULTURAL_PROJECTS, id: 'LIST' }],
    }),
    getMasterDashboardById: build.query({
      query: ({ dashboardId, params }) => ({
        url: `/dashboard/master-dashboard/${dashboardId}/`,
        params,
      }),
      transformResponse: (baseQueryReturnValue) => formatMasterDashboardData(baseQueryReturnValue),
      providesTags: (result) => (result ? [{ type: API_TAG_TYPES.MASTER_DASHBOARD, id: result.id }] : []),
    }),
    getMasterDashboardPreview: build.query({
      query: (data) => ({
        url: `/dashboard/master-dashboard/preview/`,
        method: REQUEST_METHODS.POST,
        body: data,
      }),
      transformResponse: (baseQueryReturnValue) => formatMasterDashboardData(baseQueryReturnValue),
    }),
    getMasterDashboardKeyAreaGraph: build.query({
      query: (data) => ({
        url: '/dashboard/master-dashboard/graph/',
        method: REQUEST_METHODS.POST,
        body: data,
      }),
      transformResponse: (baseQueryReturnValue) => ({
        pillarExperienceTracker: formatPillarExperienceTracker(baseQueryReturnValue.graph_data || []),
      }),
    }),
    getMasterDashboardKeyAreaHeatmap: build.query({
      query: (data) => ({
        url: '/dashboard/master-dashboard/heatmap/',
        method: REQUEST_METHODS.POST,
        body: data,
      }),
      transformResponse: (baseQueryReturnValue) => formatMasterDashboardHeatmapData(baseQueryReturnValue.heatmap_data),
    }),
    getOrganisationPortfolio: build.query({
      query: (params) => ({
        url: '/dashboard/organisation-portfolio/',
        params,
      }),
      providesTags: [{ type: API_TAG_TYPES.ORGANISATION_PORTFOLIO }],
    }),
    getOrganisationPortfolioGraph: build.query({
      query: (params) => ({
        url: '/dashboard/organisation-portfolio/graph/',
        params,
      }),
      transformResponse: ({ graph_data: graphData }) =>
        graphData.map(({ date, ...data }) => ({ date: +new Date(date), ...data })),
      providesTags: [{ type: API_TAG_TYPES.ORGANISATION_PORTFOLIO_GRAPH }],
    }),
    getOrganisationCultureProjectsOverview: build.query({
      query: (params) => ({
        url: '/dashboard/organisation-portfolio/culture-projects/',
        params,
      }),
      providesTags: [{ type: API_TAG_TYPES.ORGANISATION_PORTFOLIO_CULTURE_PROJECTS, id: 'LIST' }],
    }),
    createMasterDashboard: build.mutation({
      query: (data) => ({
        url: `/dashboard/master-dashboard/`,
        method: REQUEST_METHODS.POST,
        body: data,
      }),
      invalidatesTags: (result) => (result ? [{ type: API_TAG_TYPES.MASTER_DASHBOARD, id: 'LIST' }] : []),
    }),
    updateMasterDashboard: build.mutation({
      query: ({ dashboardId, data }) => ({
        url: `/dashboard/master-dashboard/${dashboardId}/`,
        method: REQUEST_METHODS.PATCH,
        body: data,
      }),
      invalidatesTags: (result) => (result ? [{ type: API_TAG_TYPES.MASTER_DASHBOARD, id: result.id }] : []),
    }),
    deleteMasterDashboard: build.mutation({
      query: ({ dashboardId }) => ({
        url: `/dashboard/master-dashboard/${dashboardId}/`,
        method: REQUEST_METHODS.DELETE,
      }),
      invalidatesTags: (_result, error) => (!error ? [{ type: API_TAG_TYPES.MASTER_DASHBOARD, id: 'LIST' }] : []),
    }),
  }),
});

export const {
  useGetDashboardPortfolioQuery,
  useGetPersonalDrilldownQuery,
  useGetDatasetDatesQuery,
  useGetDashboardQuery,
  useGetDrilldownQuery,
  useGetHeatmapQuery,
  useGetMasterDashboardsListQuery,
  useGetMasterDashboardProjectsQuery,
  useGetMasterDashboardByIdQuery,
  useGetMasterDashboardPreviewQuery,
  useGetMasterDashboardKeyAreaGraphQuery,
  useGetMasterDashboardKeyAreaHeatmapQuery,
  useGetOrganisationPortfolioQuery,
  useGetOrganisationPortfolioGraphQuery,
  useGetOrganisationCultureProjectsOverviewQuery,
  useCreateMasterDashboardMutation,
  useUpdateMasterDashboardMutation,
  useDeleteMasterDashboardMutation,
} = dashboardApi;
